'use client'

import { Typography } from '@/ui/atoms/typography/Typography'
import { DataTable } from '@/ui/molecules/data-table/DataTable'
import { ColumnDefinition } from '@/ui/molecules/data-table/types'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { ReactNode, useMemo, useState, useCallback, useEffect } from 'react'
import { ELendPosition } from '@/domain/euler/positions/useELendPositions'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { PointsBadge } from '@/ui/molecules/points-badge/PointsBadge'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export interface EulerCollateralRowAction {
  header: string
  render: (position: ELendPosition) => ReactNode
}

interface EulerCollateralSectionProps {
  assets: ELendPosition[]
  mode?: 'borrow' | 'spend'
  showRadioSelection?: boolean
  selectedAsset?: ELendPosition | null
  onAssetSelection?: (asset: ELendPosition | null) => void
  onTotalUSDCalculated?: (totalUSD: number) => void
  /** Show totals header ("Total deposited") above the table. Defaults to true when mode==='borrow'. */
  showTotals?: boolean
  /** Optional custom label for the totals line when shown. */
  totalsLabel?: string
  /** Optional extra action column rendered at the end of each row. */
  rowAction?: EulerCollateralRowAction
  /** Text shown when there are no rows. */
  emptyStateText?: string
}

/**
 * Component to display a single position with USD price fetching
 */
function PositionBalanceCell({
  position,
  onUSDValueUpdate,
}: {
  position: ELendPosition
  onUSDValueUpdate?: (vaultAddress: string, value: number) => void
}) {
  const { priceUSD } = useAssetPrice({
    assetAddress: position.token.address,
    vaultAddress: position.vaultAddress as `0x${string}`,
    chainId: position.chainId,
  })

  const depositAmount = Number(position.depositAmount)
  const usdValue = depositAmount * priceUSD
  const hasUsdValue = usdValue > 0

  // Notify parent of calculated USD value using useEffect to avoid infinite loops
  // We pass the vault address directly to avoid creating new callback functions
  useEffect(() => {
    if (onUSDValueUpdate) {
      onUSDValueUpdate(position.vaultAddress, usdValue)
    }

    // Cleanup: Remove this position's USD value when component unmounts
    return () => {
      if (onUSDValueUpdate) {
        onUSDValueUpdate(position.vaultAddress, 0)
      }
    }
  }, [usdValue, onUSDValueUpdate, position.vaultAddress])

  return (
    <div className="leading-tight">
      <div className="font-medium text-foreground text-sm">{depositAmount.toFixed(3)}</div>
      <div className="text-muted-foreground text-xs">{hasUsdValue ? `$${usdValue.toFixed(2)}` : '-'}</div>
    </div>
  )
}

export function EulerCollateralSection({
  assets,
  mode = 'borrow',
  showRadioSelection = false,
  selectedAsset: externalSelectedAsset,
  onAssetSelection,
  onTotalUSDCalculated,
  showTotals,
  totalsLabel = 'Total deposited',
  rowAction,
  emptyStateText = 'No collateral assets found',
}: EulerCollateralSectionProps) {
  // Track USD values for each position
  const [positionUSDValues, setPositionUSDValues] = useState<Map<string, number>>(new Map())

  // Get points data for Euler vaults
  const { getByAddress } = usePointsData()

  // Calculate total USD when individual values change - use useEffect to avoid infinite loops
  useEffect(() => {
    const total = Array.from(positionUSDValues.values()).reduce((sum, value) => sum + value, 0)
    onTotalUSDCalculated?.(total)
  }, [positionUSDValues, onTotalUSDCalculated])

  // Memoize the USD value update callback to prevent infinite loops
  const handleUSDValueUpdate = useCallback((vaultAddress: string, value: number) => {
    setPositionUSDValues((prev) => {
      const newMap = new Map(prev)
      if (value === 0) {
        // Remove the entry if value is 0 (cleanup)
        newMap.delete(vaultAddress)
      } else {
        newMap.set(vaultAddress, value)
      }
      return newMap
    })
  }, [])

  // Selection state for spend mode
  const [internalSelectedAsset, setInternalSelectedAsset] = useState<string | null>(
    externalSelectedAsset?.token?.symbol || null,
  )

  const selectedAssetSymbol = externalSelectedAsset?.token?.symbol || internalSelectedAsset

  const handleAssetSelection = useCallback(
    (assetSymbol: string) => {
      setInternalSelectedAsset(assetSymbol)
      const selectedAssetData = assets.find((position) => position.token.symbol === assetSymbol)
      onAssetSelection?.(selectedAssetData || null)
    },
    [assets, onAssetSelection],
  )

  const assetsColumnDefinition = useMemo<{ [key: string]: ColumnDefinition<ELendPosition> }>(
    () => ({
      ...(showRadioSelection && {
        select: {
          header: '',
          headerAlign: 'center',
          showOnMobile: true,
          sortable: false,
          renderCell: (position) => (
            <div className="flex justify-center">
              <div className="relative">
                <input
                  type="radio"
                  name="collateral-selection"
                  checked={selectedAssetSymbol === position.token.symbol}
                  onChange={() => handleAssetSelection(position.token.symbol)}
                  className="sr-only"
                />
                <div
                  className={`h-5 w-5 cursor-pointer rounded-full border-2 transition-all duration-200 ${
                    selectedAssetSymbol === position.token.symbol
                      ? 'border-primary bg-primary'
                      : 'border-white/20 bg-transparent hover:border-white/40'
                  }`}
                  onClick={() => handleAssetSelection(position.token.symbol)}
                >
                  {selectedAssetSymbol === position.token.symbol && (
                    <div className="absolute inset-1 rounded-full bg-foreground" />
                  )}
                </div>
              </div>
            </div>
          ),
        },
      }),
      asset: {
        header: 'Assets',
        headerAlign: 'left',
        showOnMobile: true,
        sortable: false,
        renderCell: (position) => {
          return (
            <div className="flex items-center gap-2">
              <div className="flex shrink-0">
                <TokenIcon token={position.token} className="h-6 w-6" />
              </div>
              <div className="flex min-w-0 flex-col items-start leading-tight">
                <div className="truncate font-semibold text-foreground text-sm">{position.token.symbol}</div>
                <div className="flex flex-col gap-px">
                  {position.curatorName && (
                    <span className="truncate text-[11px] text-muted-foreground leading-snug">
                      by {position.curatorName}
                    </span>
                  )}
                  {position.market && (
                    <span className="truncate text-[11px] text-muted-foreground leading-snug">
                      {position.market.name} Market
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        },
      },
      balance: {
        header: 'Balance',
        headerAlign: 'left',
        showOnMobile: true,
        sortable: true,
        sortingFn: (a, b) => {
          const aAmount = Number(a.original.depositAmount)
          const bAmount = Number(b.original.depositAmount)
          return aAmount - bAmount
        },
        renderCell: (position) => <PositionBalanceCell position={position} onUSDValueUpdate={handleUSDValueUpdate} />,
      },
      apy: {
        header: 'Deposit APY',
        headerAlign: 'right',
        showOnMobile: true,
        sortable: true,
        sortingFn: (a, b) => {
          // supplyAPY is a Percentage (BigNumber), need to convert to number for sorting
          const aAPY =
            typeof a.original.supplyAPY === 'number'
              ? a.original.supplyAPY
              : Number.parseFloat(a.original.supplyAPY.toString())
          const bAPY =
            typeof b.original.supplyAPY === 'number'
              ? b.original.supplyAPY
              : Number.parseFloat(b.original.supplyAPY.toString())
          return aAPY - bAPY
        },
        renderCell: (position) => {
          // Get points for this Euler vault
          const points = getByAddress({
            type: 'euler',
            action: 'lend',
            address: position.vaultAddress,
          })

          // supplyAPY is already a Percentage (fraction), need to multiply by 100
          const apyPercent =
            typeof position.supplyAPY === 'number'
              ? position.supplyAPY * 100
              : Number.parseFloat(position.supplyAPY.toString()) * 100

          return (
            <div className="flex items-center justify-end gap-2">
              {points?.multiplier && (
                <PointsBadge action="lend" asset={TokenSymbol(position.token.symbol)} multiplier={points.multiplier} />
              )}
              <div className="text-right">
                <div className="font-medium text-foreground text-sm">{apyPercent.toFixed(2)}%</div>
              </div>
            </div>
          )
        },
      },
      ...(rowAction && {
        action: {
          header: rowAction.header,
          headerAlign: 'right',
          showOnMobile: true,
          sortable: false,
          renderCell: (position) => (
            <div className="flex items-center justify-end">{rowAction.render(position)}</div>
          ),
        },
      }),
    }),
    [showRadioSelection, selectedAssetSymbol, handleAssetSelection, getByAddress, handleUSDValueUpdate, rowAction],
  )

  // Calculate total collateral from position USD values
  const totalCollateral = useMemo(() => {
    return Array.from(positionUSDValues.values()).reduce((sum, value) => sum + value, 0)
  }, [positionUSDValues])

  const shouldShowTotals = showTotals ?? mode === 'borrow'

  // Tailwind requires static class strings - enumerate the combinations we actually use.
  // The action column uses `auto` so it only takes the space the button needs,
  // leaving the data columns comfortable.
  let gridTemplateColumnsClassName: string
  if (showRadioSelection && rowAction) {
    gridTemplateColumnsClassName = '[grid-template-columns:60px_1.5fr_1fr_1fr_auto]'
  } else if (showRadioSelection) {
    gridTemplateColumnsClassName = '[grid-template-columns:60px_1fr_1fr_1fr]'
  } else if (rowAction) {
    gridTemplateColumnsClassName = '[grid-template-columns:1.5fr_1fr_1fr_auto]'
  } else {
    gridTemplateColumnsClassName = 'grid-cols-3'
  }

  return (
    <div>
      {shouldShowTotals && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex gap-4">
            <Typography className="font-semibold">{totalsLabel}</Typography>
            <Typography className="font-semibold">${totalCollateral.toFixed(2)}</Typography>
          </div>
        </div>
      )}

      {/* Assets Table */}
      <DataTable
        columnDef={assetsColumnDefinition}
        data={assets}
        withPanel={false}
        gridTemplateColumnsClassName={gridTemplateColumnsClassName}
        emptyStateText={emptyStateText}
      />
    </div>
  )
}
